/**
 * LINE ニュース一覧ビュー。
 * 仕様書: docs/01_spec/spec_line_bot.md A-10.5
 */
import type { LineNewsStatus } from '@goenhub/shared';
import {
  collection,
  getDocs,
  limit as limitFn,
  query,
  where,
} from 'firebase/firestore';
import { Copy, Megaphone, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { EmptyState } from '@/components/common/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useCancelLineNewsSchedule,
  useDuplicateLineNews,
  useHardDeleteLineNews,
  useLineNewsList,
  useUpdateLineNews,
  type LineNewsRecord,
} from '@/hooks/useLineNews';
import { db } from '@/lib/firebase';
import { formatDate, formatDateTime } from '@/lib/firestoreTime';


const STATUS_LABEL: Record<LineNewsStatus, string> = {
  draft: '下書き',
  review: 'レビュー中',
  scheduled: '予約済',
  sending: '送信中',
  sent: '送信済',
  failed: '失敗',
};

const STATUS_BADGE: Record<
  LineNewsStatus,
  'draft' | 'review' | 'approved' | 'sent' | 'danger' | 'default'
> = {
  draft: 'draft',
  review: 'review',
  scheduled: 'review',
  sending: 'approved',
  sent: 'sent',
  failed: 'danger',
};

interface Props {
  onCreate: () => void;
  onSelect: (news: LineNewsRecord) => void;
}

export function NewsListView({ onCreate, onSelect }: Props) {
  const { user } = useAuth();
  const canEdit = user?.role === 'owner' || user?.role === 'editor';
  const { data, isLoading } = useLineNewsList({ deleted: false, limit: 100 });
  const deleteMutation = useHardDeleteLineNews();
  const cancelMutation = useCancelLineNewsSchedule();
  const updateMutation = useUpdateLineNews();
  const duplicateMutation = useDuplicateLineNews();
  const [revertingId, setRevertingId] = useState<string | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);

  const handleDuplicate = async (n: LineNewsRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) return;
    setDuplicatingId(n.id);
    try {
      await duplicateMutation.mutateAsync({ source: n, createdBy: user.uid });
      toast.success(`「${n.title || '無題'}」を複製しました`);
    } catch (err) {
      toast.error(`複製失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleDelete = async (id: string, title: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`「${title}」を完全削除します。この操作は取り消せません。`)) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast.success('削除しました');
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleRevertToDraft = async (n: LineNewsRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    if (n.status !== 'scheduled' && n.status !== 'failed') return;
    if (
      !window.confirm(
        n.status === 'scheduled'
          ? `「${n.title || '無題'}」の配信予約をキャンセルして下書きに戻します。よろしいですか？`
          : `「${n.title || '無題'}」を下書きに戻します（失敗状態をクリア）。よろしいですか？`,
      )
    )
      return;
    setRevertingId(n.id);
    try {
      if (n.status === 'scheduled') {
        // アクティブな schedule doc を検索してキャンセル
        const snap = await getDocs(
          query(
            collection(db, 'line_news_schedules'),
            where('newsId', '==', n.id),
            where('status', '==', 'scheduled'),
            limitFn(1),
          ),
        );
        if (!snap.empty) {
          await cancelMutation.mutateAsync({
            scheduleId: snap.docs[0].id,
            newsId: n.id,
          });
        } else {
          // フォールバック：schedule 不在ならそのまま draft に戻す
          await updateMutation.mutateAsync({
            id: n.id,
            data: { status: 'draft', sendAt: null },
          });
        }
      } else {
        await updateMutation.mutateAsync({
          id: n.id,
          data: { status: 'draft', sendAt: null, lastError: null },
        });
      }
      toast.success('下書きに戻しました');
    } catch (err) {
      toast.error(
        `下書きに戻せません: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setRevertingId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-[var(--text-muted)]">
          {isLoading ? '読み込み中…' : `${data?.length ?? 0} 件`}
        </div>
        {canEdit && (
          <Button type="button" variant="primary" onClick={onCreate}>
            <Plus className="size-4 mr-1" />
            新規ニュース作成
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-12">
          <Spinner />
        </div>
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={Megaphone}
          title="ニュースがまだありません"
          description="「新規ニュース作成」から開始できます"
        />
      ) : (
        <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] overflow-hidden">
          {data.map((n) => (
            <div
              key={n.id}
              className="flex items-center gap-4 p-3 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
              onClick={() => onSelect(n)}
            >
              <Megaphone className="size-4 text-[var(--text-muted)] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-semibold text-[13px] truncate">
                    {n.title || '(無題)'}
                  </span>
                  <Badge variant={STATUS_BADGE[n.status]}>
                    {STATUS_LABEL[n.status]}
                  </Badge>
                  {n.status === 'sent' && (
                    <Badge variant="default">
                      {n.targetUserIds.length === 0
                        ? n.sentRecipientCount && n.sentRecipientCount > 0
                          ? `配信: ${n.sentRecipientCount}（全友だち）`
                          : '全友だち配信完了'
                        : n.sentRecipientCount !== null
                          ? `配信: ${n.sentRecipientCount}${
                              n.recipientCount && n.recipientCount > 0
                                ? ` / ${n.recipientCount}`
                                : ''
                            }`
                          : '配信完了'}
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-[var(--text-muted)] flex items-center gap-3">
                  <span>作成 {formatDate(n.createdAt)}</span>
                  <span>
                    配信予定:{' '}
                    {n.sendAt ? formatDateTime(n.sendAt) : '未定'}
                  </span>
                  <span>
                    宛先:{' '}
                    {n.targetUserIds.length === 0
                      ? '全友だち'
                      : `${n.targetUserIds.length} 名`}
                  </span>
                </div>
              </div>
              {canEdit && (n.status === 'scheduled' || n.status === 'failed') && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={(e) => void handleRevertToDraft(n, e)}
                  disabled={revertingId === n.id}
                  title={
                    n.status === 'scheduled'
                      ? '配信予約をキャンセルして下書きに戻す'
                      : '失敗状態をクリアして下書きに戻す'
                  }
                >
                  {revertingId === n.id ? <Spinner className="size-3.5" /> : <RotateCcw />}
                </Button>
              )}
              {canEdit && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={(e) => void handleDuplicate(n, e)}
                  disabled={duplicatingId === n.id}
                  title="このニュースを複製して下書きを作成"
                >
                  {duplicatingId === n.id ? <Spinner className="size-3.5" /> : <Copy />}
                </Button>
              )}
              {canEdit && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={(e) => void handleDelete(n.id, n.title || n.id, e)}
                >
                  <Trash2 />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
